/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RefreshCcw, Wand2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { fetchUpstreamRatios, updateSystemOption } from '../api'
import type { DifferencesMap, RatioType, UpstreamConfig } from '../types'
import { VENDOR_PRESETS, getVendorPreset } from './constants'
import {
  buildResolutionsFromUpstream,
  buildSyncUpdates,
  deleteResolutionField,
  getBillingCategory,
  parseJsonRecord,
  type ResolutionsMap,
} from './upstream-ratio-sync-helpers'
import { UpstreamRatioSyncTable } from './upstream-ratio-sync-table'

type VendorPriceSyncDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 与本页「价格摘要」列展示的是同一份数据，作为合并写入的基线 */
  modelRatios: {
    ModelPrice: string
    ModelRatio: string
    CompletionRatio: string
    CacheRatio: string
    CreateCacheRatio: string
    ImageRatio: string
    AudioRatio: string
    AudioCompletionRatio: string
    'billing_setting.billing_mode': string
    'billing_setting.billing_expr': string
  }
  /** 打开时预选的厂商（按当前渠道筛选推导），缺省为列表第一项 */
  defaultVendorId?: number
}

const VENDOR_OPTION_KEYS = [
  'ModelPrice',
  'ModelRatio',
  'CompletionRatio',
  'CacheRatio',
  'CreateCacheRatio',
  'ImageRatio',
  'AudioRatio',
  'AudioCompletionRatio',
  'billing_setting.billing_mode',
  'billing_setting.billing_expr',
] as const

export function VendorPriceSyncDialog({
  open,
  onOpenChange,
  modelRatios,
  defaultVendorId,
}: VendorPriceSyncDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [vendorId, setVendorId] = useState<number>(VENDOR_PRESETS[0].id)
  const [differences, setDifferences] = useState<DifferencesMap>({})
  const [resolutions, setResolutions] = useState<ResolutionsMap>({})
  const [hasFetched, setHasFetched] = useState(false)

  // 每次打开都回到干净状态，并按当前渠道筛选预选厂商
  useEffect(() => {
    if (!open) return
    setDifferences({})
    setResolutions({})
    setHasFetched(false)
    if (defaultVendorId && getVendorPreset(defaultVendorId)) {
      setVendorId(defaultVendorId)
    }
  }, [open, defaultVendorId])

  const vendor = getVendorPreset(vendorId)

  const parsedRatios = useMemo(() => {
    const parsed: Record<string, Record<string, number | string>> = {}
    VENDOR_OPTION_KEYS.forEach((key) => {
      parsed[key] = parseJsonRecord<number>(modelRatios[key])
    })
    return parsed
  }, [modelRatios])

  const fetchMutation = useMutation({
    mutationFn: fetchUpstreamRatios,
    onSuccess: (data) => {
      if (!data.success) {
        toast.error(data.message || t('Failed to fetch upstream prices'))
        return
      }

      const { differences: diffs, test_results } = data.data

      const errorResults = test_results.filter((r) => r.status === 'error')
      if (errorResults.length > 0) {
        const errorMsg = errorResults
          .map((r) => `${r.name}: ${r.error}`)
          .join(', ')
        toast.error(t('Some channels failed: {{errorMsg}}', { errorMsg }))
        return
      }

      setHasFetched(true)
      setDifferences(diffs)
      // 官网价源一次只查一个厂商 —— 直接全选，站长只需确认
      const autoSelected = buildResolutionsFromUpstream(diffs)
      setResolutions(autoSelected)

      const modelCount = Object.keys(autoSelected).length
      if (modelCount === 0) {
        toast.success(t('No price differences found'))
      } else {
        toast.success(
          t('Found {{count}} models with differences, all selected', {
            count: modelCount,
          })
        )
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || t('Failed to fetch upstream prices'))
    },
  })

  const { mutate: syncMutate, isPending: isSyncing } = useMutation({
    mutationFn: async (updates: Array<{ key: string; value: string }>) => {
      for (const update of updates) {
        await updateSystemOption(update)
      }
    },
    onSuccess: () => {
      toast.success(t('Prices synced successfully'))
      queryClient.invalidateQueries({ queryKey: ['system-options'] })
      queryClient.invalidateQueries({ queryKey: ['model-pricing'] })
      queryClient.invalidateQueries({ queryKey: ['channel-models-map'] })
      setDifferences({})
      setResolutions({})
      setHasFetched(false)
      onOpenChange(false)
    },
    onError: (error: Error) => {
      toast.error(error.message || t('Failed to sync prices'))
    },
  })

  const handleFetch = useCallback(() => {
    if (!vendor) return
    const upstreams: UpstreamConfig[] = [
      {
        id: vendor.id,
        // 后端会把请求名再拼成 "<name>(<id>)"，与上游差异表表头保持一致
        name: vendor.name,
        base_url: vendor.baseUrl,
        endpoint: vendor.endpoint,
      },
    ]
    setDifferences({})
    setResolutions({})
    setHasFetched(false)
    fetchMutation.mutate({ upstreams, timeout: 20 })
  }, [fetchMutation, vendor])

  const handleSelectValue = useCallback(
    (model: string, ratioType: RatioType, value: number | string) => {
      setResolutions((prev) => {
        const nextModel = { ...(prev[model] || {}) }
        const category = getBillingCategory(ratioType)
        if (category !== 'tiered') {
          Object.keys(nextModel).forEach((rt) => {
            const rtCategory = getBillingCategory(rt)
            if (rtCategory !== 'tiered' && rtCategory !== category) {
              delete nextModel[rt]
            }
          })
        }
        nextModel[ratioType] = value
        return { ...prev, [model]: nextModel }
      })
    },
    []
  )

  const handleUnselectValue = useCallback(
    (model: string, ratioType: RatioType) => {
      setResolutions((prev) => deleteResolutionField(prev, model, ratioType))
    },
    []
  )

  const handleApply = useCallback(() => {
    const updates = buildSyncUpdates(parsedRatios, resolutions)
    syncMutate(updates)
  }, [parsedRatios, resolutions, syncMutate])

  const selectedModelCount = Object.keys(resolutions).length
  const isBusy = fetchMutation.isPending || isSyncing

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[90vh] max-w-[calc(100%-2rem)] flex-col sm:max-w-[90vw] xl:max-w-[1100px]'>
        <DialogHeader>
          <DialogTitle>{t('Update prices from vendor site')}</DialogTitle>
          <DialogDescription>
            {t(
              'Fetch the official price list from a cloud vendor, convert it to local ratios and write it into the model prices. Differences are pre-selected — review before applying.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto'>
          <div className='bg-muted/40 flex flex-col gap-3 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between'>
            <div className='flex items-center gap-2'>
              <span className='text-muted-foreground shrink-0 text-sm'>
                {t('Channel type')}
              </span>
              <Select
                items={VENDOR_PRESETS.map((preset) => ({
                  value: String(preset.id),
                  label: preset.label,
                }))}
                value={String(vendorId)}
                onValueChange={(v) => {
                  if (v === null) return
                  setVendorId(Number(v))
                  setDifferences({})
                  setResolutions({})
                  setHasFetched(false)
                }}
                disabled={isBusy}
              >
                <SelectTrigger className='w-[180px]'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent alignItemWithTrigger={false}>
                  <SelectGroup>
                    {VENDOR_PRESETS.map((preset) => (
                      <SelectItem key={preset.id} value={String(preset.id)}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={handleFetch} disabled={isBusy}>
              {fetchMutation.isPending ? (
                <span className='mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
              ) : (
                <RefreshCcw className='mr-2 h-4 w-4' />
              )}
              {t('Fetch and compare')}
            </Button>
          </div>

          {hasFetched ? (
            <UpstreamRatioSyncTable
              differences={differences}
              resolutions={resolutions}
              isDisabled={isBusy}
              isSyncing={fetchMutation.isPending}
              onSelectValue={handleSelectValue}
              onUnselectValue={handleUnselectValue}
            />
          ) : (
            <div className='flex h-48 items-center justify-center rounded-md border border-dashed'>
              <div className='text-center'>
                <Wand2 className='text-muted-foreground mx-auto mb-2 h-6 w-6' />
                <p className='text-muted-foreground text-sm'>
                  {t('Pick a vendor and fetch its official price list')}
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className='sm:justify-between'>
          <span className='text-muted-foreground self-center text-xs'>
            {selectedModelCount > 0
              ? t('{{count}} models selected to update', {
                  count: selectedModelCount,
                })
              : t('Nothing selected yet')}
          </span>
          <div className='flex gap-2'>
            <Button
              variant='outline'
              onClick={() => onOpenChange(false)}
              disabled={isSyncing}
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleApply}
              disabled={selectedModelCount === 0 || isBusy}
            >
              {isSyncing && (
                <span className='mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
              )}
              {t('Apply update')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
